import { z } from "zod";
import {
  ASK_SAGE_MAX_RETENTION_DAYS,
  ASK_SAGE_MIN_RETENTION_DAYS,
} from "./askSage.js";
import {
  membershipStatusSchema,
  roleSchema,
  vaultSageCredentialKindSchema,
} from "../enums.js";
import {
  APPROVAL_SIGNATURE_TEMPLATE_MAX_LENGTH,
  unknownApprovalPlaceholders,
} from "../approvalStamp.js";
import { isoDate, optionalIsoDate } from "./common.js";

/**
 * Which VaultSage space an organization's documents physically live in.
 *
 * A Team API key is expected to store into `TEAM` (the shared team space, which
 * outlives any individual) and a Personal API key into `PERSONAL` (the key
 * owner's own drive, which does not). `UNKNOWN` means the storage has not been
 * verified against VaultSage yet; `MISSING` means verification positively
 * established that the recorded directories no longer exist, while the credential
 * itself still works.
 */
export const vaultsageStorageSpaceSchema = z.enum(["TEAM", "PERSONAL", "UNKNOWN", "MISSING"]);
export type VaultsageStorageSpace = z.infer<typeof vaultsageStorageSpaceSchema>;

export const vaultsageStorageLocationSchema = z.object({
  provisioned: z.boolean(),
  /** Where the files actually are, as last observed in VaultSage. */
  space: vaultsageStorageSpaceSchema,
  /** Where the configured credential kind implies they should be. */
  expectedSpace: vaultsageStorageSpaceSchema,
  /** False when `space` contradicts `expectedSpace`; the UI warns on this. */
  matchesCredential: z.boolean(),
  /** Human-readable location, e.g. "Team Space / ComplianceSage-dev / samsung-fire-df27bc74". */
  path: z.string().nullable(),
  ownerLabel: z.string().nullable(),
  /**
   * True for storage provisioned before the per-organization directory level
   * existed, whose folders sit directly under the environment home. Such an
   * organization keeps working; it just cannot safely share a space with another.
   */
  legacyLayout: z.boolean(),
  verifiedAt: optionalIsoDate,
});
export type VaultsageStorageLocation = z.infer<typeof vaultsageStorageLocationSchema>;

/**
 * How many references one AI review call carries. The model returns a verdict
 * for every reference it is given, so this bounds the length of a single
 * response; a source set at or below it is reviewed in one call.
 */
export const aiReviewBatchSizeSchema = z.number().int().min(5).max(200);

/**
 * How many of those calls one review runs at once. Concurrent VaultSage review
 * calls per instance is this multiplied by the worker concurrency, so it is
 * deliberately small.
 */
export const aiReviewBatchConcurrencySchema = z.number().int().min(1).max(8);

export const organizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: roleSchema,
  membershipStatus: membershipStatusSchema.nullable(),
  vaultsageCredential: z
    .object({
      configured: z.boolean(),
      kind: vaultSageCredentialKindSchema.nullable(),
      fingerprint: z.string().nullable(),
      keyPrefix: z.string().nullable(),
      /**
       * Full stored key, only on Super Admin payloads so an operator can
       * confirm the value. Null when the ciphertext cannot be decrypted.
       */
      apiKey: z.string().nullable().optional(),
      updatedAt: optionalIsoDate,
    })
    .optional(),
  vaultsageRootPath: z.string().nullable().optional(),
  vaultsageRootUrl: z.string().url().nullable().optional(),
  vaultsageStorage: vaultsageStorageLocationSchema.optional(),
  /** AI review batching, present only on Super Admin payloads. */
  aiReviewBatchSize: aiReviewBatchSizeSchema.optional(),
  aiReviewBatchConcurrency: aiReviewBatchConcurrencySchema.optional(),
});
export type Organization = z.infer<typeof organizationSchema>;

export const createOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(200),
  initialAdminEmail: z.string().email().optional(),
  vaultsageCredential: z.object({
    kind: vaultSageCredentialKindSchema,
    apiKey: z.string().trim().min(1).max(1000),
  }),
});
export type CreateOrganizationInput = z.infer<typeof createOrganizationSchema>;

/**
 * Every field is optional so the Super Admin can rename an organization and
 * tune its review batching independently, but an empty body is rejected rather
 * than silently recorded as an update that changed nothing.
 */
export const updateOrganizationSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    aiReviewBatchSize: aiReviewBatchSizeSchema.optional(),
    aiReviewBatchConcurrency: aiReviewBatchConcurrencySchema.optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: "Provide at least one field to update.",
  });
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;

export const setOrganizationVaultsageCredentialSchema = z.object({
  kind: vaultSageCredentialKindSchema,
  apiKey: z.string().trim().min(1).max(1000),
});
export type SetOrganizationVaultsageCredentialInput = z.infer<
  typeof setOrganizationVaultsageCredentialSchema
>;

export const vaultsageCredentialStatusSchema = z.object({
  configured: z.boolean(),
  kind: vaultSageCredentialKindSchema.nullable(),
  fingerprint: z.string().nullable(),
  keyPrefix: z.string().nullable(),
  apiKey: z.string().nullable().optional(),
  updatedAt: optionalIsoDate,
});
export type VaultsageCredentialStatus = z.infer<typeof vaultsageCredentialStatusSchema>;

export const selectOrganizationSchema = z.object({
  organizationId: z.string().min(1),
});
export type SelectOrganizationInput = z.infer<typeof selectOrganizationSchema>;

/**
 * Free-text signature block for the approval page appended to approved
 * documents.
 *
 * Unknown placeholders are rejected rather than rendered literally: a
 * `{{reviewrr}}` typo reaching a signed compliance document is far worse than a
 * refused save.
 */
export const approvalSignatureTemplateSchema = z
  .string()
  .min(1)
  .max(APPROVAL_SIGNATURE_TEMPLATE_MAX_LENGTH)
  .superRefine((value, ctx) => {
    const unknown = unknownApprovalPlaceholders(value);
    if (unknown.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unknown placeholder(s): ${unknown.map((name) => `{{${name}}}`).join(", ")}`,
      });
    }
  });

export const approvalSealStatusSchema = z.object({
  configured: z.boolean(),
  fileName: z.string().nullable(),
  mimeType: z.string().nullable(),
});
export type ApprovalSealStatus = z.infer<typeof approvalSealStatusSchema>;

export const organizationConfigSchema = z.object({
  autoClearEnabled: z.boolean(),
  /** When auto-clear is on, also complete AI reject outcomes for the same targets. */
  autoRejectEnabled: z.boolean(),
  /** Channels eligible for automatic self-check; empty means nothing auto-completes. */
  autoClearChannelIds: z.array(z.string().min(1)),
  /** Categories eligible for automatic self-check; parent 구분1 includes children. */
  autoClearCategoryIds: z.array(z.string().min(1)),
  /**
   * Days of Ask Sage question and answer text to keep. Null keeps it
   * indefinitely; past the window the text is blanked while the turn rows —
   * and therefore the usage statistics — remain.
   */
  askSageRetentionDays: z
    .number()
    .int()
    .min(ASK_SAGE_MIN_RETENTION_DAYS)
    .max(ASK_SAGE_MAX_RETENTION_DAYS)
    .nullable(),
  clearReviewSampleRate: z.number().min(0).max(1),
  highRiskSlaHours: z.number().int().min(1).max(720),
  reviewRequiredSlaHours: z.number().int().min(1).max(720),
  qualitySampleSlaHours: z.number().int().min(1).max(720),
  approvalSignatureTemplate: approvalSignatureTemplateSchema,
  /** Read-only: the seal is uploaded through its own multipart endpoint. */
  approvalSeal: approvalSealStatusSchema,
  updatedAt: isoDate,
});
export type OrganizationConfig = z.infer<typeof organizationConfigSchema>;

export const updateOrganizationConfigSchema = organizationConfigSchema.omit({
  updatedAt: true,
  approvalSeal: true,
});
export type UpdateOrganizationConfigInput = z.infer<typeof updateOrganizationConfigSchema>;

export const vaultsageOrphanKindSchema = z.enum(["ARCHIVED", "UNMATCHED"]);
export type VaultsageOrphanKind = z.infer<typeof vaultsageOrphanKindSchema>;

export const vaultsageOrphanFolderSchema = z.object({
  directoryId: z.string(),
  folderName: z.string(),
  kind: vaultsageOrphanKindSchema,
  organizationId: z.string().nullable(),
  organizationName: z.string().nullable(),
  archivedAt: optionalIsoDate,
  space: vaultsageStorageSpaceSchema.nullable(),
});
export type VaultsageOrphanFolder = z.infer<typeof vaultsageOrphanFolderSchema>;

export const vaultsageInaccessibleArchiveSchema = z.object({
  organizationId: z.string(),
  organizationName: z.string(),
  archivedAt: isoDate,
  directoryId: z.string().nullable(),
  folderName: z.string().nullable(),
});
export type VaultsageInaccessibleArchive = z.infer<typeof vaultsageInaccessibleArchiveSchema>;

export const vaultsageOrphanScanSchema = z.object({
  orphans: z.array(vaultsageOrphanFolderSchema),
  inaccessible: z.array(vaultsageInaccessibleArchiveSchema),
  scannedHomeCount: z.number().int().min(0),
});
export type VaultsageOrphanScan = z.infer<typeof vaultsageOrphanScanSchema>;
